import { CloseOutlineIcon, SearchOutlineIcon } from '@carboncopyco/icons';
import { InputText, type InputTextProps } from '@carboncopyco/ui';
import { useDebounce } from 'ahooks';
import classNames from 'classnames';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { InputMetadata, type InputMetadataProps } from './InputMetadata';

export interface SearchBoxInputProps
  extends Pick<
      InputTextProps,
      | 'autoFocus'
      | 'placeholder'
      | 'value'
      | 'onBlur'
      | 'onFocus'
      | 'onKeyUp'
      | 'disabled'
    >,
    Pick<InputMetadataProps, 'maxLength'> {
  onChange?: (value: string) => void;
}

export const SearchBoxInput = forwardRef<HTMLInputElement, SearchBoxInputProps>(
  (
    {
      placeholder = 'Search',
      value,
      onChange,
      onBlur,
      onFocus,
      onKeyUp,
      autoFocus,
      maxLength,
      disabled,
    },
    ref
  ) => {
    const initialRender = useRef(true);
    const [innerValue, setInnerValue] = useState<string>(value ?? '');
    const debouncedInnerValue = useDebounce(innerValue, {
      wait: 600,
    });
    const state = innerValue?.length > maxLength ? 'error' : 'normal';

    useEffect(() => {
      if (initialRender.current) {
        initialRender.current = false;
        return;
      }
      onChange?.(debouncedInnerValue);
    }, [debouncedInnerValue, onChange]);

    useEffect(() => {
      setInnerValue(value ?? '');
    }, [value]);

    return (
      <div className="group flex flex-col gap-1.5">
        <InputText
          state={state}
          ref={ref}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyUp={onKeyUp}
          autoFocus={autoFocus}
          placeholder={placeholder}
          disabled={disabled}
          leftElement={
            <span
              className={classNames(
                {
                  normal: 'text-ds-natural-300',
                  error: 'text-ds-danger-400',
                }[state],
                !disabled &&
                  {
                    normal: 'group-focus-within:text-ds-primary-300',
                    error: 'group-focus-within:text-ds-danger-400',
                  }[state]
              )}
            >
              <SearchOutlineIcon size={20} />
            </span>
          }
          rightElement={
            innerValue &&
            !disabled && (
              <span
                className={classNames(
                  'cursor-pointer',
                  {
                    normal: 'text-ds-primary-300',
                    error: 'text-ds-danger-400',
                  }[state]
                )}
                onClick={() => setInnerValue('')}
              >
                <CloseOutlineIcon size={20} />
              </span>
            )
          }
          value={innerValue}
          onChange={(event) => setInnerValue(event.target.value)}
        />
        {!!maxLength && (
          <InputMetadata
            value={innerValue}
            state={state}
            maxLength={maxLength}
          />
        )}
      </div>
    );
  }
);

SearchBoxInput.displayName = 'SearchBoxInput';
